import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { useState } from "react";
import { PermissionGate } from "@/components/PermissionGate";
import ShieldLockIcon from "../icons/ShieldLock";

interface StaffSettings {
  globalRedemptionLink: boolean;
  freezeGiftSelection: boolean;
  holdFromShopify: boolean;
  billback: boolean;
  emailThankYouNotes: boolean;
}

const TOGGLE_ROWS: {
  key: keyof StaffSettings;
  label: string;
  hint: string;
}[] = [
  {
    key: "globalRedemptionLink",
    label: "Global Redemption Link",
    hint: "A single link any recipient can use. For enterprise campaigns only — requires recipient list validation.",
  },
  {
    key: "freezeGiftSelection",
    label: "Freeze gift selection",
    hint: "Lock the collection after launch — no products can be added or removed. For fixed-budget enterprise campaigns.",
  },
  {
    key: "holdFromShopify",
    label: "Hold from Shopify",
    hint: "Prevent order creation in Shopify until manual release.",
  },
  {
    key: "billback",
    label: "Set to Billback",
    hint: "Defer payment — invoice the client after campaign completes.",
  },
  {
    key: "emailThankYouNotes",
    label: "Email thank-you notes to sender",
    hint: "Disable for large campaigns to avoid flooding the sender's inbox. Each redemption triggers an email.",
  },
];

function ToggleSwitch({
  id,
  checked,
  onChange,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      htmlFor={id}
      className="relative shrink-0 cursor-pointer"
      style={{ width: 58, height: 38 }}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      {/* Track: 34×14, centered in 58×38 touch area */}
      <span
        className="absolute rounded-full bg-smoke peer-checked:bg-dark-dust transition-colors duration-200"
        style={{ left: 12, top: 12, width: 34, height: 14 }}
      />
      {/* Thumb: 20×20, overhangs track by 3px on each side, translates 20px when checked */}
      <span
        className="absolute w-5 h-5 rounded-full bg-white peer-checked:translate-x-5 transition-transform duration-200"
        style={{ left: 9, top: 9, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}
      />
    </label>
  );
}

/**
 * Campaign-builder staff panel. Self-gating: the permission check lives here, not at the call
 * site, so adding <GfgStaffOnly /> to another step cannot leak it to senders.
 *
 * CAMPAIGN_BUILDER/view at scope "all" is held only by GFG_STANDARD_ADMIN and GFG_SUPER_ADMIN.
 * Client Admin and Org View Only hold the same subject at "org", Manager and Campaign Manager
 * fall to the "group" default, and View Only / GFG Finance do not hold it at all. Gating on
 * CAMPAIGN/edit instead would wrongly exclude GFG_STANDARD_ADMIN, whose CAMPAIGN scope is
 * "group" — only the super admin holds that one at "all".
 */
export default function GfgStaffOnly() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [settings, setSettings] = useState<StaffSettings>({
    globalRedemptionLink: false,
    freezeGiftSelection: false,
    holdFromShopify: false,
    billback: false,
    emailThankYouNotes: false,
  });

  function update(key: keyof StaffSettings, val: boolean) {
    setSettings((prev) => ({ ...prev, [key]: val }));
  }

  return (
    <PermissionGate action="view" subject="CAMPAIGN_BUILDER" scope="all">
      <div
        className="mt-8 rounded-card bg-white"
        style={{
          border: "1px solid var(--color-dark-dust)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Header */}
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="w-full flex items-center gap-6 px-5 py-5 text-left hover:bg-clear-dust transition-colors rounded-card"
        >
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: "var(--color-good-wine-4)" }}
          >
            <ShieldLockIcon sx={{ fontSize: 36, color: "var(--color-good-wine)" }} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-crimson text-[18px] text-rodeo-black leading-none">GFG Staff Only</p>
            <p className="font-lato text-[12px] text-graphite mt-1">
              Hidden from senders — visible only when logged in as GFG admin or account manager
            </p>
          </div>

          <ExpandMoreIcon
            sx={{
              fontSize: 24,
              color: "var(--color-graphite)",
              transition: "transform 0.25s ease",
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              flexShrink: 0,
            }}
          />
        </button>

        {/* Body */}
        <Collapse in={isExpanded}>
          <div style={{ height: 1, background: "var(--color-card-border)" }} />
          <div className="px-5 pb-6 pt-1">
            {TOGGLE_ROWS.map((row, idx) => (
              <div key={row.key}>
                <div className="flex items-center gap-6 py-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-lato text-lg font-semibold text-rodeo-black">{row.label}</p>
                    <p className="font-lato text-base text-graphite mt-1.5">{row.hint}</p>
                  </div>
                  <ToggleSwitch
                    id={row.key}
                    checked={settings[row.key]}
                    onChange={(val) => update(row.key, val)}
                  />
                </div>
                {idx < TOGGLE_ROWS.length - 1 && (
                  <div style={{ height: 1, background: "var(--color-card-border)" }} />
                )}
              </div>
            ))}
          </div>
        </Collapse>
      </div>
    </PermissionGate>
  );
}
